import { Switch, VStack } from '@chakra-ui/react';
import { SettingsCard, SettingsCheckTable, SettingsRow } from '../../../components/settings/primitives';
import type { LocationEntry, NexOrderingSettings } from '../../../hooks/useGetSettings';
import { useSettingsDraft } from '../SettingsDraftContext';

export interface BetaSectionProps {
    /** Delivery receive opt-in date; null when the org hasn't opted in. */
    receivingEnabledAt: string | null;
    /** NEX purchase-order opt-in as stored on the org; null when never set. */
    nexOrdering: NexOrderingSettings | null;
    /** Whether the org has a Nexsyis tenant credential. The NEX card is hidden entirely without it. */
    nexIntegrationAvailable: boolean;
    locations: LocationEntry[];
}

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Internal-only switches for features still in beta. The nav entry is gated to
 * PartBay staff (see `isInternalAdmin` in settingsNav), so everything here can
 * assume an internal admin — customers get these features when we turn them on
 * for their org, not by opting themselves in.
 */
const BetaSection = ({ receivingEnabledAt, nexOrdering, nexIntegrationAvailable, locations }: BetaSectionProps) => {
    const { getDomain, setDomain } = useSettingsDraft();

    // Receiving is stored as an activation date the server stamps, so the
    // draft carries the desired state and the date comes back on refetch.
    const receivingEnabled =
        getDomain('delivery', { receivingEnabled: !!receivingEnabledAt })?.receivingEnabled ??
        !!receivingEnabledAt;

    const receivingEnabledSince = receivingEnabledAt ? formatDate(receivingEnabledAt) : null;

    // NEX purchase orders: org switch + per-location allow-list. Both ride in
    // one draft domain so a single save writes them together.
    const nexServerValue = {
        enabled: nexOrdering?.enabled ?? false,
        locationIds: nexOrdering?.locationIds ?? [],
    };
    const nexDraft = getDomain('nexOrdering', nexServerValue) ?? nexServerValue;
    const nexEnabledSince = nexOrdering?.enabledAt ? formatDate(nexOrdering.enabledAt) : null;

    const nexDescription = nexDraft.enabled && nexEnabledSince
        ? `Posts purchase orders to Nexsyis as parts are ordered, confirmed, rejected, cancelled or returned — on every marketplace. Only the locations checked below post. Enabled ${nexEnabledSince}.`
        : 'Posts purchase orders to Nexsyis as parts are ordered, confirmed, rejected, cancelled or returned — on every marketplace. Only the locations checked below post.';

    const toggleNexLocation = (id: string, checked: boolean) => {
        const next = new Set(nexDraft.locationIds);
        if (checked) next.add(id);
        else next.delete(id);
        setDomain('nexOrdering', { enabled: nexDraft.enabled, locationIds: Array.from(next) });
    };

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Beta features"
                description="Features that are still in beta. Turning one on changes what this organization sees in the app."
            >
                <SettingsRow
                    label="Mark parts received"
                    description={
                        receivingEnabled && receivingEnabledSince
                            ? `When enabled, your team can mark parts as received on the Parts Tracker and order pages. Applies to orders created after opting in. Active for orders created on or after ${receivingEnabledSince}.`
                            : 'When enabled, your team can mark parts as received on the Parts Tracker and order pages. Applies to orders created after opting in.'
                    }
                    control={
                        <Switch
                            size="lg"
                            colorScheme="partbay-blue"
                            isChecked={receivingEnabled}
                            onChange={(e) => setDomain('delivery', { receivingEnabled: e.target.checked })}
                        />
                    }
                />
            </SettingsCard>

            {/* Only Nexsyis customers (org has a NEX tenant credential) see this card at all. */}
            {nexIntegrationAvailable && (
                <SettingsCard title="Nexsyis purchase orders" description={nexDescription}>
                    <SettingsRow
                        label="Post purchase orders to Nexsyis"
                        description="Master switch for this organization. Locations are opted in individually below."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="brand"
                                isChecked={nexDraft.enabled}
                                onChange={(e) =>
                                    setDomain('nexOrdering', { enabled: e.target.checked, locationIds: nexDraft.locationIds })
                                }
                            />
                        }
                    />
                    <SettingsCheckTable
                        columnLabel="Location"
                        checkLabel="Post to Nexsyis"
                        rows={locations.map((location) => ({
                            id: location._id,
                            label: [location.title, [location.city, location.state].filter(Boolean).join(', ')]
                                .filter(Boolean)
                                .join(' — '),
                            isChecked: nexDraft.locationIds.includes(location._id),
                            isDisabled: !nexDraft.enabled,
                            badge: location.subscriptionActive ? undefined : 'Inactive',
                        }))}
                        onToggle={toggleNexLocation}
                        emptyMessage="No locations on this organization yet."
                    />
                </SettingsCard>
            )}
        </VStack>
    );
};

export default BetaSection;
